import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useImages } from '../context/ImageContext';
import { useProducts } from '../context/ProductsContext';
import { API_URL } from '../config/api';
import { cartStorage, CartItem } from '../utils/cartStorage';
import { pushAddToCart } from '../utils/gtm';
import Footer from '../components/Footer';
import { OpenGraphHead } from '../components/OpenGraphHead';
import './ProductDetailPage.css';

interface ProductData {
  id: string;
  name: string;
  price: {
    monthly: string;
    annual: string;
  };
  images: string[];
  description: string;
  features: string[];
  detailSection: {
    title: string;
    description: string;
    image: string;
    detailFeatures: string[];
  };
  howItWorksSection?: {
    title: string;
    description: string;
    image: string;
    features: string[];
  };
  systemIntegrationsSection?: {
    title: string;
    description: string;
    image: string;
    features: string[];
  };
  technicalSpecs: string[];
  shippingDetails: string[];
  refundPolicy: string[];
}

const products: { [key: string]: ProductData } = {
  'ez-care': {
    id: 'ez-care',
    name: 'EZ Care In-Home Medical Alert',
    price: {
      monthly: '$29.99',
      annual: '$19.99'
    },
    images: [
      'ez-care-in-home-medical-alert-1.jpg',
      'ez-care-in-home-medical-alert-2.jpg',
      'ez-care-in-home-medical-alert-3.jpg',
      'ez-care-in-home-medical-alert-4.jpg'
    ],
    description: 'Landline required. EZ Care has universal landline capability and will work on traditional hard-wired lines as well as VoIP lines. Wireless systems are also available; please see our EZ Care Plus or LifeBeacon.',
    features: [
      'Push the medical alert button, connect to our 24/7 Command Center',
      'You decide who we call in an emergency',
      'Showerproof medical alert button',
      '400 ft range around the base unit'
    ],
    detailSection: {
      title: 'At Home Protection for Peace of Mind',
      description: 'Make your home a place of safety and security with EZ Care. Our in-home emergency alert button can be worn as a pendant or on a wristband—whatever suits your style!',
      image: 'ez-care-in-home-medical-alert-text-box-1-circle-2.png',
      detailFeatures: [
        'Automatic weekly tests ensure that the EZ Care is working at all times',
        'Home-based protection with a 400 foot range around the base unit',
        'Lightweight and showerproof help button',
        'Optional integration with smoke alarm, CO2 alarm, and motion detectors'
      ]
    },
    howItWorksSection: {
      title: 'How Does It Work?',
      description: 'Press the emergency alert button 24/7 to reach live help within 28 seconds (average response time). You will connect with a trained agent and communicate through the base unit. Our agents remain on the line with you until help arrives, so you never face an emergency alone.',
      image: 'ez-care-in-home-medical-alert-text-box-2-circle.webp',
      features: [
        'Press the button and communicate through the base unit\'s 2-way speaker.',
        'Battery lasts up to 3 years.',
        'The monitoring center agents stay on the line with you until help arrives.',
        'Both emergency personnel and personal contacts can be notified each time you need help.'
      ]
    },
    systemIntegrationsSection: {
      title: 'Should I Add the Optional System Integrations?',
      description: 'The EZ Care in-home system integrates with home smoke alarms, carbon monoxide alarms, and motion detectors, providing you with a safer home environment.',
      image: 'ez-care-in-home-medical-alert-text-box-3-circle.png',
      features: [
        'Smoke Detector: monitored through the base unit and sends an alert if triggered',
        'Carbon Monoxide Alarm: monitored through the base unit and sends an alert if triggered',
        'Motion Detectors: monitored through the base unit and sends an alert if triggered',
        'If an alert is sent and you are unable to communicate with us, we will send help'
      ]
    },
    technicalSpecs: [
      'Landline required.',
      'Dimensions: 7.25 in x 5.25 in x 1.1 in',
      'Weight: Base unit: 8.1 ounces; Pendant: 0.4 ounces',
      'Battery: 3 year battery life',
      'Range: 400 feet from the base unit'
    ],
    shippingDetails: [
      'Delivery: Free shipping',
      'Setup: A simple online form is all that\'s needed to set up the station. No on-device programming.'
    ],
    refundPolicy: [
      'Alert1 does not require long term contracts. Any user may cancel service at any time by returning the Alert1 equipment. If you agree to a contract of a specified length (e.g. annual plans) for a discount, you may cancel early but Alert1 does not offer refunds unless you have passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.'
    ]
  },
  'ez-care-plus': {
    id: 'ez-care-plus',
    name: 'EZ Care Plus In-Home Medical Alert',
    price: {
      monthly: '$39.99',
      annual: '$34.99'
    },
    images: [
      'ez-care-plus-in-home-medical-alert-1.jpg',
      'ez-care-in-home-medical-alert-2.jpg',
      'ez-care-in-home-medical-alert-3.jpg',
      'ez-care-in-home-medical-alert-4.jpg'
    ],
    description: 'No landline required. EZ Care Plus is our cellular medical alert system that works anywhere in your home without a phone line. Enhanced home protection with advanced monitoring capabilities.',
    features: [
      'Push the medical alert button, connect to our 24/7 Command Center',
      'You decide who we call in an emergency',
      'Showerproof medical alert button',
      '400 ft range around the base unit',
      'No phone line required - works on cellular network',
      'Cellular connectivity - works even during power outages'
    ],
    detailSection: {
      title: 'At Home Protection for Peace of Mind',
      description: 'Make your home a place of safety and security with EZ Care Plus. Our in-home emergency alert button can be worn as a pendant or on a wristband—whatever suits your style! No phone line required.',
      image: '596InHomeWomanWrist.webp',
      detailFeatures: [
        'Automatic weekly tests ensure that the EZ Care Plus is working at all times',
        'Home-based protection with a 400 foot range around the base unit',
        'Lightweight and showerproof help button',
        'No landline required - uses cellular network',
        'Optional integration with smoke alarm, CO2 alarm, and motion detectors'
      ]
    },
    technicalSpecs: [
      'No landline required - uses cellular network (AT&T/T-Mobile).',
      'Dimensions: 7.25 in x 5.25 in x 1.1 in',
      'Weight: Base unit: 8.1 ounces; Pendant: 0.4 ounces',
      'Battery: 3 year battery life in pendant; base unit has backup battery',
      'Range: 400 feet from the base unit'
    ],
    shippingDetails: [
      'Delivery: Free shipping',
      'Setup: A simple online form is all that\'s needed to set up the station. No on-device programming.'
    ],
    refundPolicy: [
      'Alert1 does not require long term contracts. Any user may cancel service at any time by returning the Alert1 equipment. If you agree to a contract of a specified length (e.g. annual plans) for a discount, you may cancel early but Alert1 does not offer refunds unless you have passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.'
    ]
  },
  'lifebeacon': {
    id: 'lifebeacon',
    name: 'LifeBeacon On-the-Go Pendant/Wristband Alert',
    price: {
      monthly: '$44.99',
      annual: '$34.99'
    },
    images: [
      'lifebeacon-on-the-go-1.jpg',
      'lifebeacon-on-the-go-2.jpg',
      'lifebeacon-on-the-go-3.jpg',
      'lifebeacon-on-the-go-4.png',
      'lifebeacon-on-the-go-5.png',
      'lifebeacon-on-the-go-6.png'
    ],
    description: 'No landline required. Mobile system with built-in GPS locator and optional Fall Detection.',
    features: [
      'Mobile system with built-in GPS locator; optional Fall Detection',
      'Lightweight and comfortable',
      'Communicate with trained agents directly through the device',
      'Wearable options include lanyard and wristband (included)',
      'Water resistant up to 1 meter'
    ],
    detailSection: {
      title: 'Versatile Alert Can Be Worn as a Pendant or Wristband',
      description: 'Peace of mind can be yours knowing that help is always available, 24/7, at the press of a button. Our mobile emergency alert button can be worn as a pendant or on a wristband—whatever suits your style!',
      image: 'wristband-pdp-bottom-transparent-1.webp',
      detailFeatures: [
        'This versatile emergency alert system with GPS location tracking can be used in all 50 US states, Canada, and Puerto Rico',
        'LifeBeacon notifies loved ones immediately of your need for help via text or email along with a map link of your location',
        'Works on the AT&T network',
        'Water resistant and should be worn in the shower',
        'Optional fall detection feature is available and provides the highest level of security'
      ]
    },
    howItWorksSection: {
      title: 'How Does It Work?',
      description: 'Press the emergency alert button 24/7 to reach live help within 28 seconds (average response time). You will connect with a trained agent who will assess the situation and send the appropriate assistance. Our agents stay connected to you until help arrives, so you never face an emergency alone.',
      image: 'wristband-pdp-bottom-transparent-2.webp',
      features: [
        'Press the button and communicate through the device\'s 2-way speaker.',
        'Rechargeable battery lasts up to 10 days.',
        'Our monitoring center agents stay on the line with you until help arrives on site.',
        'LifeBeacon will audibly notify you when you\'ve activated it, when to charge it, and more.',
        'Both emergency personnel and personal contacts can be notified each time you need help.'
      ]
    },
    systemIntegrationsSection: {
      title: 'Should I Add the Optional Fall Detection Feature?',
      description: 'All Alert1 medical alert systems have buttons to press to get help fast in an emergency. Fall detection is an optional, added device feature that measures speed, impact, and lack of movement to recognize that a fall has occurred and send an alert for help even if you are unable to press the button.',
      image: 'wristband-pdp-bottom-transparent-3.webp',
      features: [
        'Unlocking this feature is an additional $5/month',
        'This device is one of the only wristband alerts available with Fall Detection',
        'Perfect for anyone seeking the highest level of security',
        'Unit comes with a "cancel" feature in the event a fall is detected and you do not require assistance',
        'If an alert is sent and you are unable to communicate with us, we will send help'
      ]
    },
    technicalSpecs: [
      'Dimensions: 62mm x 41mm x 15.5mm',
      'Weight: 37 g (without lanyard or bracelet)',
      'Battery: 800 mAh battery - rechargeable, lasts up to 10 days',
      'GPS: Built-in GPS location tracking',
      'Water resistant up to 1 meter'
    ],
    shippingDetails: [
      'Delivery: Free shipping',
      'Setup: Comes pre-programmed. Simply charge if needed, send a test alert, and the device is ready to use.'
    ],
    refundPolicy: [
      'Alert1 does not require long term contracts. Any user may cancel service at any time by returning the Alert1 equipment. If you agree to a contract of a specified length (e.g. annual plans) for a discount, you may cancel early but Alert1 does not offer refunds unless you have passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.'
    ]
  }
};

const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { getImage, getImageThumbnail } = useImages();
  const { getProductByShortName } = useProducts();
  
  const [selectedImage, setSelectedImage] = useState(0);
  const [paymentPlan, setPaymentPlan] = useState<'monthly' | 'annual'>('annual');
  const [fallDetection, setFallDetection] = useState<boolean | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Get product from API by shortName (which matches the productId URL param)
  const apiProduct = productId ? getProductByShortName(productId) : null;
  
  // Get static content for this product (fallback for sections not in database)
  const product = productId ? products[productId] : null;

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <h1>Product not found</h1>
          <button onClick={() => navigate('/pricing')}>Back to Pricing</button>
        </div>
      </div>
    );
  }

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  
  const handleOrderNow = () => {
    if (apiProduct && apiProduct.pricingPlans) {
      // Find the matching plan based on selected payment plan
      const matchingPlan = apiProduct.pricingPlans.find((plan: any) => 
        plan.billingCycle === paymentPlan
      );
      
      if (matchingPlan) {
        // Get product image
        const productImage = getImage(apiProduct.imageFilename || '');
        
        // Create cart item for the product
        const cartItem: CartItem = {
          id: `product-${apiProduct.id}-${matchingPlan.id}`,
          type: 'product',
          name: apiProduct.name,
          imageUrl: productImage,
          planId: matchingPlan.id,
          planName: matchingPlan.name,
          productId: apiProduct.id,
          productShortName: apiProduct.shortName,
          fallDetection: fallDetection === true || undefined
        };
        
        // Add product to cart
        cartStorage.addItem(cartItem);
        console.log('[ProductDetail] Added product to cart:', cartItem);

        const price = (matchingPlan as any).todayTotal ?? matchingPlan.annualTotal ?? matchingPlan.monthlyPrice ?? 0;
        pushAddToCart({
          currency: 'USD',
          value: price,
          items: [{
            item_id: cartItem.id,
            item_name: cartItem.name,
            price,
            quantity: 1,
            index: 0,
          }],
        });
        
        // Get all cart items including the one we just added
        const allCartItems = cartStorage.getItems();
        console.log('[ProductDetail] All cart items:', allCartItems);
        
        // Navigate to checkout with cart items
        navigate('/checkout', { 
          state: { 
            cartItems: allCartItems,
            planId: matchingPlan.id, // Keep for backward compatibility
            fallDetection: fallDetection === true // Only include if explicitly selected
          } 
        });
      } else {
        alert('Pricing plan not available. Please try again or call us at 1-877-579-9862');
      }
    } else {
      alert('Loading pricing information... Please try again in a moment.');
    }
  };

  // Get current price from API - always use database pricing
  const getCurrentPrice = () => {
    if (apiProduct && apiProduct.pricingPlans) {
      const plan = apiProduct.pricingPlans.find((p: any) => p.billingCycle === paymentPlan);
      if (plan) {
        // Add $5/month if fall detection is selected
        const basePrice = plan.monthlyPrice;
        const finalPrice = fallDetection === true ? basePrice + 5 : basePrice;
        return `$${finalPrice.toFixed(2)}`;
      }
    }
    // If no API product, show loading/error state
    return '$0.00';
  };
  
  const currentPrice = getCurrentPrice();
  const priceNote = paymentPlan === 'annual' ? '(when paying annually)' : '/mo';

  const productImageUrl = product.images?.length ? getImage(product.images[0]) : undefined;

  return (
    <div className="product-detail-page">
      <OpenGraphHead
        title={product.name}
        description={product.description}
        imagePath={productImageUrl || '/logo512.png'}
        pathOverride={`/products/${productId}`}
        imageAlt={product.name}
      />
      <section className="product-detail-section">
        <div className="container">
          <div className="product-detail-grid">
            {/* Left Side - Image Gallery + Additional Members */}
            <div className="product-gallery">
              <div className="gallery-layout">
                <div className="thumbnail-strip-vertical">
                  {product.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={getImageThumbnail(img) || getImage(img)}
                      alt={`${product.name} view ${idx + 1}`}
                      className={`thumbnail ${selectedImage === idx ? 'active' : ''}`}
                      onClick={() => setSelectedImage(idx)}
                    />
                  ))}
                </div>
                
                <div className="main-image-container">
                  {(() => {
                    const mainUrl = getImage(product.images[selectedImage]);
                    const thumbUrl = getImageThumbnail(product.images[selectedImage]);
                    const src = mainUrl ? `${mainUrl}?v=2` : undefined;
                    if (src) {
                      fetch(`${API_URL}/api/debug/log`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          location: 'ProductDetailPage',
                          message: 'Main image URL',
                          url: src,
                          isThumbnailUrl: src.includes('/thumbnail'),
                          mainUrl,
                          thumbUrl,
                          filename: product.images[selectedImage],
                          timestamp: Date.now(),
                        }),
                      }).catch(() => {});
                    }
                    return (
                      <img
                        src={src}
                        alt={product.name}
                        className="main-product-image"
                        onLoad={(e) => {
                          const el = e.target as HTMLImageElement;
                          fetch(`${API_URL}/api/debug/log`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              location: 'ProductDetailPage',
                              message: 'Main image onLoad',
                              url: src,
                              naturalW: el.naturalWidth,
                              naturalH: el.naturalHeight,
                              displayW: el.clientWidth,
                              displayH: el.clientHeight,
                              filename: product.images[selectedImage],
                              timestamp: Date.now(),
                            }),
                          }).catch(() => {});
                        }}
                      />
                    );
                  })()}
                  <button 
                    className="gallery-nav prev"
                    onClick={() => setSelectedImage(selectedImage > 0 ? selectedImage - 1 : product.images.length - 1)}
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button 
                    className="gallery-nav next"
                    onClick={() => setSelectedImage(selectedImage < product.images.length - 1 ? selectedImage + 1 : 0)}
                    aria-label="Next image"
                  >
                    ›
                  </button>
                </div>
              </div>
              
              {/* Additional Members Call-out */}
              <div className="additional-members-callout">
                <p>
                  <strong>Adding additional members?</strong><br />
                  Call us today at <a href="tel:1-877-579-9862">1-877-579-9862</a>
                </p>
              </div>
            </div>

            {/* Right Side - Product Info */}
            <div className="product-info">
              <div className="product-header">
                <h1>{product.name}</h1>
              </div>

              <div className="product-pricing">
                <h2 className="price">
                  {currentPrice}<span className="price-note">{priceNote}</span>
                </h2>
                <p className="no-fees">No Equipment Fees! Free Activation! Free Shipping!</p>
                <p className="cancel-text">Cancel Any Time</p>
              </div>

              <p className="product-description">{product.description}</p>

              {/* Plan Selector */}
              <div className="plan-selector">
                <h3>Step 1: Select Your Plan</h3>
                <div className="plan-buttons">
                  <button
                    className={`plan-btn ${paymentPlan === 'monthly' ? 'active' : 'inactive'}`}
                    onClick={() => setPaymentPlan('monthly')}
                  >
                    PAY MONTHLY
                  </button>
                  <button
                    className={`plan-btn ${paymentPlan === 'annual' ? 'active' : 'inactive'}`}
                    onClick={() => setPaymentPlan('annual')}
                  >
                    PRE-PAY FOR ANNUAL
                  </button>
                </div>
              </div>

              {/* Fall Detection Selector */}
              <div className="fall-detection-selector">
                <h3>Step 2. Add Fall Detection for only $5/mo more?</h3>
                <div className="fall-detection-buttons">
                  <button
                    className={`fall-detection-btn no-thanks ${
                      fallDetection === null ? 'highlighted' : 
                      fallDetection === false ? 'active' : 'inactive'
                    }`}
                    onClick={() => setFallDetection(false)}
                  >
                    NO THANKS
                  </button>
                  <button
                    className={`fall-detection-btn yes-please ${
                      fallDetection === null ? 'highlighted' : 
                      fallDetection === true ? 'active' : 'inactive'
                    }`}
                    onClick={() => setFallDetection(true)}
                  >
                    YES PLEASE
                  </button>
                </div>
              </div>

              {/* Order Button */}
              <button 
                className="order-now-btn"
                onClick={handleOrderNow}
              >
                ORDER NOW
              </button>

              {/* Features List */}
              <ul className="product-features">
                {product.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>

              {/* Expandable Sections */}
              <div className="expandable-sections">
                <div className="expandable-section">
                  <button 
                    className={`section-header ${expandedSection === 'specs' ? 'expanded' : ''}`}
                    onClick={() => toggleSection('specs')}
                  >
                    <span>Technical Specifications</span>
                    <span className="expand-icon">{expandedSection === 'specs' ? '−' : '+'}</span>
                  </button>
                  {expandedSection === 'specs' && (
                    <div className="section-content">
                      <div className="section-text">
                        {product.technicalSpecs.map((spec, idx) => (
                          <p key={idx}>{spec}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="expandable-section">
                  <button 
                    className={`section-header ${expandedSection === 'shipping' ? 'expanded' : ''}`}
                    onClick={() => toggleSection('shipping')}
                  >
                    <span>Shipping Details</span>
                    <span className="expand-icon">{expandedSection === 'shipping' ? '−' : '+'}</span>
                  </button>
                  {expandedSection === 'shipping' && (
                    <div className="section-content">
                      <div className="section-text">
                        {product.shippingDetails.map((detail, idx) => (
                          <p key={idx}>{detail}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="expandable-section">
                  <button 
                    className={`section-header ${expandedSection === 'refund' ? 'expanded' : ''}`}
                    onClick={() => toggleSection('refund')}
                  >
                    <span>Refund Policy</span>
                    <span className="expand-icon">{expandedSection === 'refund' ? '−' : '+'}</span>
                  </button>
                  {expandedSection === 'refund' && (
                    <div className="section-content">
                      <div className="section-text">
                        {product.refundPolicy.map((policy, idx) => (
                          <p key={idx}>{policy}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Detail Section - Image Left, Content Right */}
      <section className="product-detail-section-two">
        <div className="container">
          <div className="detail-content-grid">
            <div className="detail-image-wrapper">
              <img 
                src={getImage(product.detailSection.image)} 
                alt={product.detailSection.title}
                className="detail-circular-image"
              />
            </div>
            <div className="detail-text-content">
              <h2>{product.detailSection.title}</h2>
              <p className="detail-description">{product.detailSection.description}</p>
              <ul className="detail-features-list">
                {product.detailSection.detailFeatures.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How Does It Work Section - Text Left, Image Right */}
      {product.howItWorksSection && (
        <section className="product-detail-section-three">
          <div className="container">
            <div className="detail-content-grid-reverse">
              <div className="detail-text-content">
                <h2>{product.howItWorksSection.title}</h2>
                <p className="detail-description">{product.howItWorksSection.description}</p>
                <ul className="detail-features-list">
                  {product.howItWorksSection.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
              <div className="detail-image-wrapper">
                <img 
                  src={getImage(product.howItWorksSection.image)} 
                  alt={product.howItWorksSection.title}
                  className="detail-circular-image"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* System Integrations Section - Image Left, Text Right */}
      {product.systemIntegrationsSection && (
        <section className="product-detail-section-four">
          <div className="container">
            <div className="detail-content-grid">
              <div className="detail-image-wrapper">
                <img 
                  src={getImage(product.systemIntegrationsSection.image)} 
                  alt={product.systemIntegrationsSection.title}
                  className="detail-circular-image"
                />
              </div>
              <div className="detail-text-content">
                <h2>{product.systemIntegrationsSection.title}</h2>
                <p className="detail-description">{product.systemIntegrationsSection.description}</p>
                <ul className="detail-features-list">
                  {product.systemIntegrationsSection.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetailPage;

